import type { NextPage } from 'next';
import React from 'react';
import { Sticky, StickyContainer } from 'react-sticky';

import { Layout } from '@sravni/react-design-system';
import { useIsMobile } from '@sravni/react-utils';

import type { Application } from '@src/@types/app';
import type { AffSub4 } from '@src/@types/microcredits';
import { Breadcrumbs } from '@src/components/Breadcrumbs';
import { ActionButton } from '@src/components/item/ActionButton';
import { CommonInformation } from '@src/components/item/CommonInformation';
import ProductInfo from '@src/components/item/ProductInfo';
import { StickyActionsBlock } from '@src/components/item/StickyActionsBlock';
import { StickyBlock } from '@src/components/item/StickyBlock';
import { Summary } from '@src/components/item/Summary';
import { MicrocreditCalculator } from '@src/components/MicrocreditCalculator';
import { Pixel } from '@src/components/Pixel';
import { TOP_PAGE_ANCHOR } from '@src/constants/common';
import { DEFAULT_FILTERS, DEFAULT_REGION_ROUTE, MODES } from '@src/constants/filters';
import { ListKeys } from '@src/constants/microcredits';
import { removeRouteParams, stringifyPath } from '@src/helpers/query';
import { currentLocationSelector } from '@src/reducers/locations';
import { fetchOffers } from '@src/reducers/microcredits';
import { fetchCardAdvertisingIfNeeded, fetchCardIfNeeded, selectCardState, useCard } from '@src/reducers/product';
import { Layout as AppLayout } from 'app/Layout';
import { ErrorPage } from 'pageModules/ErrorPage';

import styles from './ItemPage.module.scss';

interface IPageProps {
  error?: {
    statusCode: number;
    statusMessage: string;
  };
}

export const ItemPage: NextPage<IPageProps> = ({ error }) => {
  const card = useCard();
  const isMobile = useIsMobile();
  const { name, organization, alias, advertising, _id } = card;
  const monetization = advertising?.monetization;
  const breadcrumbs = [
    { title: 'Займы', url: '/zaimy/' },
    { title: organization?.name, url: `/zaimy/mfo/${organization?.alias}/mikrokredit/${alias}/` },
  ];

  if (error) {
    return <ErrorPage statusCode={error.statusCode} />;
  }

  if (!organization || !name) {
    return <ErrorPage statusCode={500} />;
  }

  const affSub4: AffSub4 = {
    organizationName: organization.name,
    productName: name,
    organizationId: organization._id,
    productId: _id,
  };

  return (
    <AppLayout>
      <StickyContainer id={TOP_PAGE_ANCHOR}>
        <Sticky>
          {(props) => (
            <div>
              <StickyActionsBlock {...props} />
            </div>
          )}
        </Sticky>

        {monetization?.pixels?.display ? (
          <Pixel source="search_product" link={monetization.pixels.display} affSub4={affSub4} />
        ) : null}

        {breadcrumbs && !isMobile && (
          <div className={styles.card_container}>
            <Layout className={styles.layout}>
              <Breadcrumbs className={styles.breadcrumbs} homeTitle="Сравни.ру" items={breadcrumbs || []} />
            </Layout>
          </div>
        )}

        <div className={styles.card_container}>
          <Layout className={styles.layout}>
            <h1 className={styles.title}>{`Микрокредит «${name}» от МФО ${organization.name}`}</h1>
          </Layout>
        </div>

        <Summary className={styles.summary} />

        <StickyContainer>
          <Sticky disableCompensation>
            {(props) => (
              <div>
                <StickyBlock
                  name={name}
                  organization={organization}
                  isPartner={card.isBankPartner || card.advertising?.isPartner}
                  {...props}
                />
              </div>
            )}
          </Sticky>

          <MicrocreditCalculator />
          <ProductInfo />
          <CommonInformation />
        </StickyContainer>

        {isMobile ? (
          <div className={styles.sticky_button}>
            <ActionButton />
          </div>
        ) : null}
      </StickyContainer>
    </AppLayout>
  );
};

ItemPage.getInitialProps = async (context: Application.ReduxNextPageContext) => {
  const {
    store: { dispatch, getState },
    query,
    res,
  } = context;
  const queryOrganizationAlias = query.organizationAlias as string | undefined;
  const queryProductAlias = query.productAlias as string | undefined;
  const { route } = currentLocationSelector(getState());

  if (queryOrganizationAlias && queryProductAlias) {
    await Promise.all([
      dispatch(
        fetchCardIfNeeded({
          organizationAlias: queryOrganizationAlias,
          alias: queryProductAlias,
        }),
      ),
      dispatch(
        fetchOffers({
          key: ListKeys.BESTPRODUCTS,
          query: DEFAULT_FILTERS[MODES.BESTPRODUCTS],
          params: {
            location: route || DEFAULT_REGION_ROUTE,
          },
        }),
      ),
    ]);
  }

  const cardState = selectCardState(getState());

  if (cardState?.error?.response?.status && res) {
    const {
      error: {
        response: { status, statusText },
      },
    } = cardState;

    res.statusCode = status;

    return { error: { statusCode: status, statusMessage: statusText } };
  }

  const { source: { _id: sourceId, alias: productAlias, organization } = {} } = cardState;

  if (res && organization && (productAlias !== queryProductAlias || organization.alias !== queryOrganizationAlias)) {
    res.writeHead(302, {
      Location: stringifyPath(
        `/zaimy/mfo/${organization.alias}/mikrokredit/${productAlias}/`,
        removeRouteParams(query),
      ),
    });

    res.end();

    return {};
  }

  if (sourceId) {
    await dispatch(fetchCardAdvertisingIfNeeded({ id: sourceId, location: route }));
  }

  return {};
};
